import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { searchProducts } from "../../services/catalogService";
import { trackEvent } from "../../services/analyticsService";
import "../../styles/Header.css";

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;
const SUGGESTION_LIMIT = 15;

const Header = () => {
  const [searchInput, setSearchInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Monotonic id so slow responses for an older query never overwrite newer ones.
  const requestSeqRef = useRef(0);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    const query = searchInput.trim();

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (query.length < MIN_QUERY_LENGTH) {
      requestSeqRef.current += 1; // Invalidate any in-flight request.
      setSuggestions([]);
      setIsSearching(false);
      setShowDropdown(false);
      return;
    }

    setShowDropdown(true);
    setIsSearching(true);

    debounceTimerRef.current = setTimeout(() => {
      const seq = ++requestSeqRef.current;
      searchProducts(query, { limit: SUGGESTION_LIMIT })
        .then((response) => {
          // Ignore stale results from an earlier keystroke.
          if (seq !== requestSeqRef.current) return;
          setSuggestions(response.results || []);
          setIsSearching(false);
        })
        .catch(() => {
          if (seq !== requestSeqRef.current) return;
          setSuggestions([]);
          setIsSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput]);

  const handleSuggestionClick = (product) => {
    trackEvent("select_product_search_result", {
      product_number: product.productNumber,
      product_brand: product.brand,
    });
    navigate(`/products/${product.productNumber}`);
    setSearchInput("");
    setSuggestions([]);
    setShowDropdown(false);
    setIsMenuOpen(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchInput) {
      navigate(`/products/${searchInput}`);
      setShowDropdown(false);
      setIsMenuOpen(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const query = searchInput.trim();
  const showResults = showDropdown && query.length >= MIN_QUERY_LENGTH;

  return (
    <header className="header">
      <div className="logo">
        <Link to="/" onClick={closeMenu}>
          <img src="/assets/WibiTeclogo.png" className="logo" alt="Logo" />
        </Link>
      </div>
      <form onSubmit={handleSearchSubmit} className="search-form">
        <input
          type="text"
          placeholder="Search by product name or part number"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="search-input"
        />
        <button type="submit" className="search-button">
          <i className="fas fa-search"></i>
        </button>
        {showResults && (
          <div className="suggestions-dropdown">
            {isSearching && suggestions.length === 0 && (
              <div className="suggestion-item">Searching...</div>
            )}
            {!isSearching && suggestions.length === 0 && (
              <div className="suggestion-item">No results found</div>
            )}
            {suggestions.map((item, index) => (
              <div
                key={item.id || index}
                className="suggestion-item"
                onClick={() => handleSuggestionClick(item)}
              >
                {item.productTitle} - {item.productNumber}
              </div>
            ))}
          </div>
        )}

        {/* Hamburger Menu */}
        <div
          className={`hamburger ${isMenuOpen ? "open" : ""}`}
          onClick={toggleMenu}
        >
          <div className="bar"></div>
          <div className="bar"></div>
          <div className="bar"></div>
        </div>
      </form>

      {/* Navigation */}
      <nav>
        <ul className={`nav-links ${isMenuOpen ? "active" : ""}`}>
          <li>
            <Link to="/" onClick={closeMenu}>
              Home
            </Link>
          </li>
          <li>
            <Link to="/products-page" onClick={closeMenu}>
              Products
            </Link>
          </li>
          <li>
            <Link to="/products" onClick={closeMenu}>
              Brands
            </Link>
          </li>
          <li>
            <Link to="/about-us" onClick={closeMenu}>
              About Us
            </Link>
          </li>
          <li>
            <Link to="/contact" onClick={closeMenu}>
              Contact
            </Link>
          </li>
        </ul>
      </nav>
    </header>
  );
};

export default Header;
